import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Send } from "lucide-react";
import ReferralSection from "./ReferralSection";
import BulkWhatsAppDialog from "./BulkWhatsAppDialog";

const FIELDS_BASIC = [
  { key: "name", label: "Nombre *", required: true, type: "text" },
  { key: "primer_apellido", label: "Primer apellido" },
  { key: "segundo_apellido", label: "Segundo apellido" },
  { key: "dni", label: "DNI / NIE" },
  { key: "codigo_expediente", label: "Código expediente (140N)", pattern: "^\\d+N$", placeholder: "140N", uppercase: true },
  { key: "phone", label: "Teléfono *", required: true, type: "tel" },
  { key: "telefono_emergencia", label: "Teléfono de emergencia" },
  { key: "email", label: "Email", type: "email" },
  { key: "birth_date", label: "Fecha de nacimiento", type: "date" },
];

const FIELDS_ADDRESS = [
  { key: "tipo_via", label: "Tipo vía" },
  { key: "nombre_via", label: "Nombre vía" },
  { key: "numero_via", label: "Número" },
  { key: "bloque", label: "Bloque" },
  { key: "portal", label: "Portal" },
  { key: "escalera", label: "Esc." },
  { key: "planta", label: "Planta" },
  { key: "puerta", label: "Puerta" },
  { key: "codigo_postal", label: "Código postal" },
  { key: "localidad", label: "Localidad" },
  { key: "municipio", label: "Municipio" },
  { key: "provincia", label: "Provincia" },
];

const FIELDS_EXTRA = [
  { key: "nacionalidad", label: "Nacionalidad" },
  { key: "pais_nacimiento", label: "País de nacimiento" },
];

const EMPTY = Object.fromEntries(
  [...FIELDS_BASIC, ...FIELDS_ADDRESS, ...FIELDS_EXTRA, { key: "notes" }, { key: "recognition_center_id" }, { key: "assigned_teacher" }, { key: "student_type" }, { key: "program_type" }, { key: "list_tags" }, { key: "has_special_conditions" }].map(
    (f) => [f.key, f.key === "has_special_conditions" ? false : f.key === "list_tags" ? [] : ""]
  )
);

function StudentField({ f, value, onChange }) {
  const handleChange = (e) => {
    let v = e.target.value;
    if (f.uppercase) v = v.toUpperCase().trim();
    onChange({ target: { value: v } });
  };
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-700 mb-1 block">{f.label}</label>
      <input
        data-testid={`student-${f.key}`}
        type={f.type || "text"}
        value={value}
        onChange={handleChange}
        required={f.required}
        pattern={f.pattern}
        placeholder={f.placeholder}
        className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
        style={f.uppercase ? { fontFeatureSettings: "'tnum'" } : undefined}
      />
      {f.pattern && value && !new RegExp(f.pattern).test(value) && (
        <div className="text-[10px] text-red-600 mt-1">
          Formato inválido. Ejemplo: 140N
        </div>
      )}
    </div>
  );
}

export default function StudentDialog({ open, student, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [showBulk, setShowBulk] = useState(false);
  // V1.38 · Desplegable de profesores
  const [teacherOptions, setTeacherOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // V1.38 · Fuente única de profesores = `/teachers`.
        const { data } = await api.get("/teachers", { params: { include_inactive: false } });
        if (cancelled) return;
        const seen = new Set();
        const opts = (Array.isArray(data) ? data : [])
          .filter((p) => p.active !== false)
          .map((p) => {
            const parts = (p.name || "").trim().split(/\s+/);
            const code = ((parts[0] || "").charAt(0) + (parts[1] || "").charAt(0)).toUpperCase();
            return { code, label: p.name || code };
          })
          .filter((o) => o.code && !seen.has(o.code) && (seen.add(o.code), true));
        setTeacherOptions(opts);
      } catch (_) { if (!cancelled) setTeacherOptions([]); }
    })();
    return () => { cancelled = true; };
  }, []);
  const [tab, setTab] = useState("basicos");
  // V1.71 · Etiquetas de listado (lookup de Configuración).
  const [listTags, setListTags] = useState([]);
  useEffect(() => {
    api.get("/settings/lookup/listado_filters")
      .then(({ data }) => setListTags(data?.values || []))
      .catch(() => setListTags([]));
  }, []);
  const [saving, setSaving] = useState(false);
  const [centers, setCenters] = useState([]);

  useEffect(() => {
    if (open) {
      const hydrate = (src) => {
        const next = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => {
          const v = src?.[k];
          // V1.38 · Preserva tipos (bool para has_special_conditions).
          if (typeof EMPTY[k] === "boolean") next[k] = !!v;
          else if (Array.isArray(EMPTY[k])) next[k] = Array.isArray(v) ? v : [];
          else next[k] = v ?? "";
        });
        setForm(next);
      };
      hydrate(student);
      setTab("basicos");
      // V1.89 · Relee la ficha al abrir: si el alumno completó su alta digital
      // desde el móvil, aquí se ven sus datos sin recargar la página.
      if (student?.id) {
        api.get(`/students/${student.id}`)
          .then(({ data }) => { if (data?.id) hydrate(data); })
          .catch(() => {});
      }
      // Load recognition centers for the "Otros" selector
      api.get("/recognition-centers")
        .then(({ data }) => setCenters(data || []))
        .catch(() => setCenters([]));
    }
  }, [open, student]);

  // V1.25.3 · Datos del centro seleccionado (para mostrar Nº DGT read-only)
  const selectedCenter = centers.find((c) => c.id === form.recognition_center_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    setSaving(true);
    try {
      if (student?.id) {
        await api.patch(`/students/${student.id}`, form);
        toast.success("Alumno actualizado");
      } else {
        await api.post("/students", form);
        toast.success("Alumno creado");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => (
    <StudentField
      key={f.key}
      f={f}
      value={form[f.key]}
      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
    />
  );

  const tabs = [
    { k: "basicos", label: "Datos básicos" },
    { k: "direccion", label: "Dirección" },
    { k: "extra", label: "Otros" },
    // V1.25 · Referidos ahora integrado en la ficha del alumno.
    // Solo se muestra cuando el alumno ya existe (tiene id).
    ...(student?.id ? [{ k: "referidos", label: "Referidos" }] : []),
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="student-dialog">
        <DialogHeader>
          <DialogTitle>{student?.id ? "Editar alumno" : "Nuevo alumno"}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-neutral-200 mb-3">
          {tabs.map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`student-tab-${t.k}`}
              className={
                "px-3 py-2 text-sm font-semibold transition-colors border-b-2 " +
                (tab === t.k
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-neutral-500 hover:text-neutral-800")
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {tab === "basicos" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS_BASIC.map(renderField)}
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-neutral-700 mb-1 block">Observaciones</label>
                <textarea
                  data-testid="student-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          )}
          {tab === "direccion" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {FIELDS_ADDRESS.map(renderField)}
            </div>
          )}
          {tab === "extra" && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FIELDS_EXTRA.map(renderField)}
              </div>
              {/* V1.38 · Operativa del profesor + V1.37 tipo alumno + condiciones */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 block">Profesor asignado</label>
                  <select
                    value={form.assigned_teacher || ""}
                    onChange={(e) => setForm({ ...form, assigned_teacher: e.target.value })}
                    data-testid="student-assigned-teacher"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">— Sin asignar —</option>
                    {teacherOptions.map((o) => (
                      <option key={o.code} value={o.code}>{o.label} ({o.code})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 block">Tipo de alumno</label>
                  <select
                    value={form.student_type || ""}
                    onChange={(e) => setForm({ ...form, student_type: e.target.value })}
                    data-testid="student-type-select"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">— Sin asignar —</option>
                    <option value="recorrido_completo">Recorrido completo</option>
                    <option value="traslado">Traslado</option>
                    <option value="recuperacion_puntos">Recuperación de puntos</option>
                    <option value="reciclaje">Reciclaje</option>
                    <option value="otros">Otros</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700 mb-1 block">Programa de prácticas (Kai)</label>
                  <select
                    value={form.program_type || ""}
                    onChange={(e) => setForm({ ...form, program_type: e.target.value })}
                    data-testid="student-program-type"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">— Sin definir (Kai usa recorrido completo) —</option>
                    <option value="programa_30">Programa 30 (2·3·3·3·3·3·4·4·3·2)</option>
                    <option value="completo">Recorrido completo (10 → 2·2·3·3 · 5 → 3·2)</option>
                    <option value="repetidor">Repetidor (10 → 4·3·3 · 5 → 3·2)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700 mb-1 block">
                  Etiquetas de listado (hoja imprimible)
                </label>
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="student-list-tags">
                  {listTags.map((t) => {
                    const on = (form.list_tags || []).includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        data-testid={`student-list-tag-${t}`}
                        onClick={() => setForm({
                          ...form,
                          list_tags: on
                            ? (form.list_tags || []).filter((x) => x !== t)
                            : [...(form.list_tags || []), t],
                        })}
                        className={"h-7 rounded-full px-2.5 text-[11px] font-bold uppercase border transition-colors " +
                          (on ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-neutral-300 text-neutral-600 hover:border-orange-300")}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-neutral-500 mt-1">
                  Se gestionan en Configuración › Filtros de listado (o desde la propia hoja imprimible).
                </p>
              </div>
              <label className="flex items-center gap-2 text-xs font-semibold text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!form.has_special_conditions}
                  onChange={(e) => setForm({ ...form, has_special_conditions: e.target.checked })}
                  data-testid="student-special-conditions"
                />
                Alumno con condiciones especiales
                <span className="ml-1 text-[10px] font-normal text-amber-800">(sólo activar el flag; los detalles NO se muestran a profesores por LOPDGDD)</span>
              </label>

              {/* V1.25.3 · Centro de Reconocimiento Médico DGT */}
              <div className="rounded-lg border border-neutral-200 bg-neutral-50/60 p-3 space-y-2" data-testid="student-recognition-center">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-xs font-semibold text-neutral-700">
                    Centro de Reconocimiento Médico
                  </label>
                  <span className="text-[10px] text-neutral-500">
                    Autorrelleno en documentos DGT
                  </span>
                </div>
                <select
                  value={form.recognition_center_id}
                  onChange={(e) => setForm({ ...form, recognition_center_id: e.target.value })}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="student-rc-select"
                >
                  <option value="">— Sin asignar —</option>
                  {centers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.recognition_center_number ? ` · ${c.recognition_center_number}` : ""}
                    </option>
                  ))}
                </select>
                {selectedCenter && (
                  <div className="rounded-md bg-white border border-neutral-200 px-3 py-2 text-xs" data-testid="student-rc-details">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] font-semibold uppercase text-neutral-500">Nº Centro DGT</span>
                      {selectedCenter.recognition_center_number ? (
                        <span className="font-mono font-bold text-emerald-700" data-testid="student-rc-number">
                          {selectedCenter.recognition_center_number}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-mono text-amber-700 text-[11px]" data-testid="student-rc-number-missing">
                          <AlertTriangle className="h-3 w-3" /> Sin configurar
                        </span>
                      )}
                    </div>
                    {selectedCenter.address && (
                      <div className="text-neutral-600 truncate">{selectedCenter.address}</div>
                    )}
                    {!selectedCenter.recognition_center_number && (
                      <div className="mt-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px] px-2 py-1 leading-snug" data-testid="student-rc-warning">
                        El Centro de Reconocimiento seleccionado no tiene Número Centro DGT configurado.
                        Complete el dato en Configuración → Centros de Reconocimiento DGT antes de generar documentación oficial.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {tab === "referidos" && student?.id && (
            <div data-testid="student-referrals-tab">
              <ReferralSection studentId={student.id} studentName={student.name || ""} />
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            {/* V1.35 · Envío agrupado de próximas citas (sólo en edición) */}
            {student?.id && (
              <button
                type="button"
                onClick={() => setShowBulk(true)}
                className="btn-secondary"
                data-testid="student-bulk-whatsapp"
                title="Enviar próximas sesiones del alumno en un enlace"
              >
                <Send className="h-4 w-4" />
                Enviar próximas sesiones
              </button>
            )}
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="student-cancel-button">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="student-save-button">
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </DialogFooter>
        </form>

        {student?.id && (
          <BulkWhatsAppDialog
            open={showBulk}
            onClose={() => setShowBulk(false)}
            studentId={student.id}
            studentName={student.name || ""}
            phone={student.phone || ""}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
